import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import yaml from 'js-yaml';
import { API_URL, USER_POOL_ID, USER_POOL_CLIENT_ID } from '../config';
import {
  selectableReleases,
  latestStable,
  organizationScopes,
  buildRows,
  applyTargetRevision,
} from '../utils/versionUpdate';
import './OrganizationManager.css';
import './VersionUpdater.css';

const userPool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId: USER_POOL_CLIENT_ID,
});

// How many repos are written at once. GitHub commits are per repo, so a small
// number keeps the run quick without hammering the orchestrator.
const CONCURRENCY = 3;

function claimsOf(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const raw = payload['cognito:groups'];
    const groups = Array.isArray(raw) ? raw : String(raw || '').replace(/^\[|\]$/g, '').split(',').map((s) => s.trim()).filter(Boolean);
    return { email: payload.email || '', isAdmin: groups.includes('Admin') };
  } catch (e) {
    return { email: '', isAdmin: false };
  }
}

function getSessionToken() {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) return reject(new Error('Not signed in'));
    cognitoUser.getSession((err, session) => {
      if (err || !session || !session.isValid()) return reject(err || new Error('Session expired'));
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

async function apiFetch(token, path, init = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { Authorization: token, ...(init.headers || {}) },
  });
  if (!res.ok) {
    let text = '';
    try { text = await res.text(); } catch (e) { /* ignore */ }
    let message = text;
    try { message = JSON.parse(text).error || text; } catch (e) { /* not JSON */ }
    throw new Error(message || `HTTP ${res.status}`);
  }
  return res;
}

/** Change one repo's targetRevision through the same endpoints the config page uses. */
async function updateRepoVersion(token, repo, target) {
  const current = await apiFetch(token, `/rho-configuration?repo=${encodeURIComponent(repo)}`);
  const text = await current.text();
  const doc = yaml.load(text);
  const { changed, previous } = applyTargetRevision(doc, target);
  if (!changed) return { status: 'unchanged', previous };
  const body = yaml.dump(doc, { quotingType: '"', forceQuotes: false });
  const res = await apiFetch(token, `/rho-configuration?repo=${encodeURIComponent(repo)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'text/yaml' },
    body,
  });
  let result = {};
  try { result = await res.json(); } catch (e) { /* body may be empty */ }
  return { status: 'done', previous, licenseRestored: !!result.license_restored };
}

const DIRECTION_LABEL = { upgrade: 'Upgrade', downgrade: 'Downgrade', change: 'Change' };

export default function VersionUpdater() {
  const navigate = useNavigate();
  const tokenRef = useRef(null);
  const [me, setMe] = useState({ email: '', isAdmin: false });
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);

  const [data, setData] = useState({ deployments: {}, licenses: {}, institutions: {}, archived: {} });
  const [releases, setReleases] = useState([]);
  const [scopeKey, setScopeKey] = useState('');
  const [target, setTarget] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  // repo -> { status: 'queued'|'updating'|'done'|'unchanged'|'failed', error?, previous?, licenseRestored? }
  const [progress, setProgress] = useState({});
  const [refreshingCache, setRefreshingCache] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setBanner(null);
    try {
      const token = await getSessionToken();
      tokenRef.current = token;
      const who = claimsOf(token);
      setMe(who);
      if (!who.isAdmin) {
        // Admin-only for now: distributors are sent back to the installations page.
        navigate('/', { replace: true });
        return;
      }
      const [reposRes, relRes] = await Promise.all([
        apiFetch(token, `/repos?t=${Date.now()}`),
        apiFetch(token, '/releases'),
      ]);
      const repos = await reposRes.json();
      const rel = await relRes.json();
      setData({
        deployments: repos.deployments || {},
        licenses: repos.licenses || {},
        institutions: repos.institutions || {},
        archived: repos.archived || {},
      });
      setReleases(Array.isArray(rel.tags) ? rel.tags : []);
    } catch (err) {
      setBanner({ kind: 'error', text: `Could not load installations: ${err.message}` });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const scopes = useMemo(() => organizationScopes(data.deployments), [data.deployments]);
  const choices = useMemo(() => selectableReleases(releases, me.isAdmin), [releases, me.isAdmin]);
  const latest = useMemo(() => latestStable(releases), [releases]);

  // Default scope: 16 Bit's own repos for admins, the only scope for distributors.
  useEffect(() => {
    if (!scopes.length) return;
    setScopeKey((k) => (k && scopes.some((s) => s.key === k) ? k : scopes[0].key));
  }, [scopes]);
  useEffect(() => {
    if (!target && latest) setTarget(latest);
  }, [latest, target]);

  const scope = scopes.find((s) => s.key === scopeKey) || null;
  const rows = useMemo(() => buildRows({
    repos: scope ? scope.repos : [],
    licenses: data.licenses,
    institutions: data.institutions,
    archived: data.archived,
    target,
  }).filter((r) => !r.archived) // archived installations are frozen; keep them out of the list
    .sort((a, b) => a.repo.localeCompare(b.repo)), [scope, data, target]);

  // Changing scope or target clears the selection and any finished run.
  useEffect(() => {
    setSelected(new Set());
    setConfirming(false);
    if (!running) setProgress({});
  }, [scopeKey, target]); // eslint-disable-line react-hooks/exhaustive-deps

  const eligible = rows.filter((r) => r.eligible);
  const selectedRows = rows.filter((r) => selected.has(r.repo) && r.eligible);
  const downgrades = selectedRows.filter((r) => r.direction === 'downgrade').length;
  const allEligibleSelected = eligible.length > 0 && eligible.every((r) => selected.has(r.repo));

  const toggle = (repo) => {
    if (running) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(repo)) next.delete(repo); else next.add(repo);
      return next;
    });
    setConfirming(false);
  };
  const toggleAll = () => {
    if (running) return;
    setSelected(allEligibleSelected ? new Set() : new Set(eligible.map((r) => r.repo)));
    setConfirming(false);
  };

  const run = async () => {
    if (!selectedRows.length || !target) return;
    setConfirming(false);
    setRunning(true);
    setBanner(null);
    const queue = selectedRows.map((r) => r.repo);
    setProgress(Object.fromEntries(queue.map((repo) => [repo, { status: 'queued' }])));
    const token = tokenRef.current;
    let done = 0; let unchanged = 0; let failed = 0; let restored = 0;

    const worker = async () => {
      while (queue.length) {
        const repo = queue.shift();
        setProgress((p) => ({ ...p, [repo]: { status: 'updating' } }));
        try {
          const r = await updateRepoVersion(token, repo, target);
          if (r.status === 'done') { done += 1; if (r.licenseRestored) restored += 1; } else unchanged += 1;
          setProgress((p) => ({ ...p, [repo]: r }));
        } catch (err) {
          failed += 1;
          setProgress((p) => ({ ...p, [repo]: { status: 'failed', error: err.message || String(err) } }));
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));

    const parts = [`${done} updated to ${target}`];
    if (unchanged) parts.push(`${unchanged} already there`);
    if (failed) parts.push(`${failed} failed`);
    if (restored) parts.push(`${restored} had a licence change reverted`);
    setBanner({ kind: failed ? 'error' : 'ok', text: parts.join(', ') + '.' });
    setRunning(false);

    // Refresh the repo cache so the installations table shows the new versions,
    // then reload this page's data. Best effort: the commits are already made.
    if (done) {
      setRefreshingCache(true);
      try {
        await apiFetch(token, `/repos/refresh?t=${Date.now()}`, { method: 'POST' });
      } catch (err) {
        setBanner((b) => ({ kind: b ? b.kind : 'ok', text: `${b ? b.text : ''} The repository cache could not be refreshed (${err.message}); use Refresh Cache on the installations page.` }));
      }
      try {
        const reposRes = await apiFetch(token, `/repos?t=${Date.now()}`);
        const repos = await reposRes.json();
        setData({
          deployments: repos.deployments || {},
          licenses: repos.licenses || {},
          institutions: repos.institutions || {},
          archived: repos.archived || {},
        });
      } catch (e) { /* the page still shows the per-row results */ }
      setRefreshingCache(false);
    }
    setSelected(new Set());
  };

  const stateOf = (row) => {
    const p = progress[row.repo];
    if (p) return p;
    if (row.onTarget) return { status: 'on-target' };
    return null;
  };

  return (
    <div className="orgs-page vup-page">
      <div className="orgs-header">
        <div className="orgs-header__brand">
          <img src="/16bit-primary-logo-reverse.svg" alt="16 Bit" />
          <div className="orgs-header__title">
            <div className="orgs-header__eyebrow">Distributor Management System</div>
            <h1>Update Rho Versions</h1>
          </div>
        </div>
        <div className="orgs-header__actions">
          <button type="button" className="orgs-btn" onClick={() => navigate('/')}>← Installations</button>
          <button type="button" className="orgs-btn" onClick={load} disabled={loading || running}>Reload</button>
        </div>
      </div>

      {banner && (
        <div className={`orgs-banner orgs-banner--${banner.kind}`} role="status">
          <span>{banner.text}{refreshingCache ? ' Refreshing the repository cache…' : ''}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setBanner(null)}>×</button>
        </div>
      )}

      <p className="orgs-note">
        Pick the installations to move and the release to move them to. Each selected repository gets a
        commit that changes only <code>targetRevision</code> in its rho.yaml; the site picks it up on its next
        sync. Leave a site unticked to leave it alone.
      </p>

      <div className="vup-controls">
        {(
          <label className="vup-field">
            <span>Repositories owned by</span>
            <select value={scopeKey} onChange={(e) => setScopeKey(e.target.value)} disabled={loading || running}>
              {scopes.map((s) => (
                <option key={s.key} value={s.key}>{s.label} ({s.repos.filter((r) => !data.archived[r]).length})</option>
              ))}
            </select>
          </label>
        )}
        <label className="vup-field">
          <span>Target release</span>
          <select value={target} onChange={(e) => setTarget(e.target.value)} disabled={loading || running || !choices.length}>
            {!choices.length && <option value="">No releases found</option>}
            {choices.map((tag) => (
              <option key={tag} value={tag}>{tag}{tag === latest ? ' (latest stable)' : ''}</option>
            ))}
          </select>
        </label>
        {latest && target && target !== latest && (
          <button type="button" className="orgs-btn orgs-btn--link" onClick={() => setTarget(latest)} disabled={running}>
            Use latest ({latest})
          </button>
        )}
      </div>

      {loading ? (
        <div className="orgs-empty">Loading installations…</div>
      ) : !scope ? (
        <div className="orgs-empty">No installations to update.</div>
      ) : (
        <div className="org-card">
          <div className="vup-card-header">
            <div>
              <strong>{scope.label}</strong>
              <span className="orgs-muted"> · {rows.length} repositor{rows.length === 1 ? 'y' : 'ies'}, {eligible.length} can move to {target || '—'}</span>
            </div>
            <div className="vup-actions">
              {confirming ? (
                <div className="orgs-confirm vup-confirm">
                  <span>
                    Set <strong>{selectedRows.length}</strong> repositor{selectedRows.length === 1 ? 'y' : 'ies'} to <strong>{target}</strong>?
                    {downgrades > 0 && <em> {downgrades} of them would be a downgrade.</em>}
                  </span>
                  <button type="button" className="orgs-btn orgs-btn--small orgs-btn--primary" onClick={run}>Yes, update</button>
                  <button type="button" className="orgs-btn orgs-btn--small" onClick={() => setConfirming(false)}>Cancel</button>
                </div>
              ) : (
                <button
                  type="button"
                  className="orgs-btn orgs-btn--primary"
                  disabled={!selectedRows.length || !target || running}
                  onClick={() => setConfirming(true)}
                >
                  {running ? 'Updating…' : `Update ${selectedRows.length || ''} selected`}
                </button>
              )}
            </div>
          </div>

          <table className="orgs-table vup-table">
            <thead>
              <tr>
                <th className="vup-check">
                  <input
                    type="checkbox"
                    aria-label="Select every repository that can be updated"
                    checked={allEligibleSelected}
                    disabled={!eligible.length || running}
                    onChange={toggleAll}
                  />
                </th>
                <th>Repository</th>
                <th>Institution</th>
                <th>Current</th>
                <th>Change</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="orgs-empty">This organization owns no active repositories.</td></tr>
              )}
              {rows.map((row) => {
                const st = stateOf(row);
                const checked = selected.has(row.repo) && row.eligible;
                return (
                  <tr
                    key={row.repo}
                    className={`vup-row${row.eligible ? '' : ' vup-row--inert'}${checked ? ' vup-row--selected' : ''}`}
                    onClick={() => row.eligible && toggle(row.repo)}
                  >
                    <td className="vup-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.repo}`}
                        checked={checked}
                        disabled={!row.eligible || running}
                        onChange={() => toggle(row.repo)}
                      />
                    </td>
                    <td className="vup-repo">{row.repo}</td>
                    <td>{row.institution || <span className="orgs-muted">—</span>}</td>
                    <td className="vup-mono">{row.current || <span className="orgs-muted">not set</span>}</td>
                    <td>
                      {row.onTarget ? (
                        <span className="orgs-muted">Already on {target}</span>
                      ) : (
                        <span className={`vup-dir vup-dir--${row.direction || 'change'}`}>
                          {DIRECTION_LABEL[row.direction] || 'Change'} → {target}
                        </span>
                      )}
                    </td>
                    <td>
                      {st && st.status === 'queued' && <span className="orgs-muted">Queued</span>}
                      {st && st.status === 'updating' && <span className="vup-result vup-result--busy">Updating…</span>}
                      {st && st.status === 'done' && (
                        <span className="vup-result vup-result--ok">
                          Updated{st.previous ? ` from ${st.previous}` : ''}{st.licenseRestored ? ' (licence change reverted)' : ''}
                        </span>
                      )}
                      {st && st.status === 'unchanged' && <span className="orgs-muted">Already there</span>}
                      {st && st.status === 'failed' && <span className="vup-result vup-result--bad" title={st.error}>Failed: {st.error}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
